import { useMemo } from "react"
import { formatDateString } from "@/v2-common/utils/utils.date"
import cn from "@/v2-ui/utils/utils.cn"
import Icon from "@/v2-ui/icon"
import Separator from "@/v2-ui/separator"
import { errorToast } from "@/v2-ui/toast"
import Table, {
  createColumns,
  summaryFraction,
  type TableProps,
} from "@/v2-backoffice-client/components/table"
import Badge from "@/v2-backoffice-client/components/badge"
import LicenseStatusBadge from "@/v2-backoffice-client/license/LicenseStatusBadge"
import LicenseSeatBadge from "@/v2-backoffice-client/license/LicenseSeatBadge"
import LicensePlanDeprecatedBadge from "@/v2-backoffice-client/license/plan/LicensePlanDeprecatedBadge"
import LicenseTableRowToolbar from "@/v2-backoffice-client/license/LicenseTableRowToolbar"
import LicenseCreateButton from "@/v2-backoffice-client/license/create/LicenseCreateButton"
import { useLicenseStatusesQuery } from "@/v2-backoffice-client/license/license.statuses.query"
import {
  LICENSE_SOURCE_FENERUM,
  LICENSE_STATUS_COLUMN_SIZE,
} from "@/v2-backoffice-client/license/license.constants"
import {
  isLicenseLive,
  occupiedSeatsOf,
} from "@/v2-backoffice-client/license/license.utils"
import {
  groupSeatHoldersByLicense,
  seatPersonNameOf,
  type LicenseSeatHolder,
} from "@/v2-backoffice-client/license/seats/license.seats.utils"
import type { LicenseFragment } from "@/v2-backoffice-client/license/__types__/license.fragment"

type LicenseAddOnGrant = NonNullable<
  LicenseTableProps["customerAddOns"]
>[number]

function createLicenseColumns(input: {
  statusNamesByCode: Map<number, string>
  addOnsByLicenseId: Map<number, LicenseAddOnGrant[]>
  seatHoldersByLicenseId: Map<number, LicenseSeatHolder[]>
}) {
  const { statusNamesByCode, addOnsByLicenseId, seatHoldersByLicenseId } = input

  function statusName(license: LicenseFragment) {
    return (
      statusNamesByCode.get(license.LicenseStatusCode) ??
      String(license.LicenseStatusCode)
    )
  }

  return createColumns<LicenseFragment>({
    enableSorting: true,
    enableHiding: true,
    columns: {
      PlanName: {
        id: "PlanName",
        label: "License",
        // The license is known by its catalog name — that is the column a salesperson
        // scans first, so it leads the row and hosts the row toolbar.
        defaultSize: 130,
        // Push: the license row lives inside a panel, so the license panel
        // stacks on top of it (breadcrumb back). Operator licenses have no
        // license behind them — no link.
        to: ({ row }) =>
          row.original.PlanId ? `>>?v=plan:${row.original.PlanId}` : undefined,
        renderCell({ row }) {
          const { PlanName, PlanDeprecatedAt } = row.original
          // A license with no catalog name (an add-ons-only grant) leads with a
          // dash rather than an empty first cell.
          if (!PlanName) return <span className="text-muted-foreground">—</span>
          if (!PlanDeprecatedAt) return PlanName
          return (
            <span className="flex min-w-0 items-center gap-1.5">
              <span className="truncate">{PlanName}</span>
              <LicensePlanDeprecatedBadge deprecatedAt={PlanDeprecatedAt} />
            </span>
          )
        },
        exportValue({ row }) {
          return row.original.PlanName ?? ""
        },
      },
      ProductName: {
        id: "ProductName",
        label: "Product",
        defaultSize: 110,
        enableGrouping: true,
      },
      LicenseStatusCode: {
        id: "LicenseStatusCode",
        label: "Status",
        defaultSize: LICENSE_STATUS_COLUMN_SIZE,
        renderCell({ row }) {
          return (
            <LicenseStatusBadge
              license={row.original}
              statusName={statusNamesByCode.get(row.original.LicenseStatusCode)}
            />
          )
        },
        exportValue({ row }) {
          return statusName(row.original)
        },
        summaryValue({ row }) {
          return statusName(row.original)
        },
      },
      TrialEndsAt: {
        id: "TrialEndsAt",
        label: "Trial Ends",
        defaultSize: 90,
        renderCell({ row }) {
          const value = row.original.TrialEndsAt
          return value ? formatDateString(value) : null
        },
      },
      Seats: {
        id: "Seats",
        label: "Seats",
        description:
          "Occupied seats (active users + open invitations) against the licensed cap; for a seated product (the MCP servers) the people named on the license. No cap = uncapped. A pen glyph marks an operator override on a billing-derived license — hover it for what billing says.",
        defaultSize: 80,
        renderCell({ row }) {
          const license = row.original
          return (
            <span className="flex items-center gap-1.5">
              <LicenseSeatBadge
                occupied={occupiedSeatsOf(license)}
                cap={license.Seats}
                tooltip={
                  license.SeatsAreAssigned
                    ? license.Seats != null &&
                      license.SeatsAssigned > license.Seats
                      ? "More people named than licensed seats"
                      : "People named on the license against the licensed seats"
                    : undefined
                }
              />
              {license.SeatsOverride != null && (
                <Icon
                  src="user-pen"
                  size={12}
                  className="shrink-0 text-muted-foreground"
                  aria-label={`Operator override — billing says ${license.BillingSeats ?? "no cap"}`}
                />
              )}
            </span>
          )
        },
        exportValue({ row }) {
          const license = row.original
          const occupied = occupiedSeatsOf(license)
          return `${occupied} / ${license.Seats ?? "—"}`
        },
        summaryValue({ row }) {
          const license = row.original
          const occupied = license.UserCount + license.PendingInvitationCount
          return summaryFraction(occupied, license.Seats)
        },
      },
      SeatHolders: {
        id: "SeatHolders",
        label: "Seat holders",
        description:
          "Who has what the license carries. Every license covers everyone at the customer until it is limited; a seated license (the MCP servers) can be limited to named people from the row menu, Seats, and then lists them here. A license whose catalog license includes no packages reaches nobody, and says so.",
        // Two names to a line: a holder list reads as people, not a column
        // of chips, and the row stays short for the usual two to four.
        defaultSize: 230,
        getValue: (row) =>
          (seatHoldersByLicenseId.get(row.Id) ?? [])
            .map(seatPersonNameOf)
            .join(", "),
        renderCell({ row }) {
          const license = row.original
          // An ended license reaches nobody; saying who it covered reads as
          // "they still have it" (FUS-2766, Bonum).
          if (!isLicenseLive(license)) {
            return <span className="text-muted-foreground">—</span>
          }
          // A license grants through its catalog license's packages; with
          // none, "Everyone" would be a lie (FUS-2766, Parg).
          if (license.PlanPackageCount === 0 && license.SeatsAreAssigned) {
            return (
              <Badge
                variant="critical"
                iconSrc="package"
                tooltip={
                  license.PlanId == null
                    ? "This grant is on no catalog license, so it carries no packages and lets nobody in. Pick one in the row menu, Change License."
                    : "The catalog license this grant is on includes no packages, so it lets nobody in. Add its packages on Licensing → Licenses, or pick another in the row menu, Change License."
                }
              >
                Grants nothing: no packages
              </Badge>
            )
          }
          if (!license.SeatsAreAssigned || !license.SeatsRestricted) {
            return (
              <span
                className="text-muted-foreground"
                title={
                  license.SeatsAreAssigned
                    ? "Not limited: every active user of the customer has what this license carries. Limit it to named people from the row menu, Seats."
                    : "Every user of the customer has what this license carries."
                }
              >
                Everyone
              </span>
            )
          }
          const holders = seatHoldersByLicenseId.get(license.Id) ?? []
          if (holders.length === 0) {
            // Limited, but no name belongs to an active person: the license
            // reaches its named people only, i.e. nobody right now.
            return (
              <span
                className="text-muted-foreground"
                title="Limited to named people, but everyone named has left, been blocked or deleted: nobody can use this license. Name people or remove the limit (row menu, Seats)."
              >
                Stale names only
              </span>
            )
          }
          return (
            <div className="flex flex-wrap gap-1">
              {holders.map((holder) => (
                <Badge key={holder.UserId} variant="neutral">
                  {seatPersonNameOf(holder)}
                </Badge>
              ))}
            </div>
          )
        },
      },
      StartedAt: {
        id: "StartedAt",
        label: "Started",
        defaultSize: 90,
        renderCell({ row }) {
          const value = row.original.StartedAt
          return value ? formatDateString(value) : null
        },
      },
      LicenseSourceCode: {
        id: "LicenseSourceCode",
        label: "Source",
        description:
          "Operator licenses are managed here; Fenerum licenses are derived from billing subscriptions and read-only.",
        defaultSize: 80,
        enableGrouping: true,
        getValue: (row) =>
          row.LicenseSourceCode === LICENSE_SOURCE_FENERUM
            ? "Fenerum"
            : "Operator",
        // The bank is Fenerum (billing owns the row, the sync rewrites it);
        // the person is an operator grant made here.
        renderCell({ row }) {
          const isFenerum =
            row.original.LicenseSourceCode === LICENSE_SOURCE_FENERUM
          return (
            <Badge
              variant="outline"
              iconSrc={isFenerum ? "bank" : "user"}
              tooltip={
                isFenerum
                  ? "Derived from a Fenerum subscription and rewritten by the hourly sync. Change it in Fenerum; Seats and History work here."
                  : "Granted here in the Backoffice. Every row action applies."
              }
            >
              {isFenerum ? "Fenerum" : "Operator"}
            </Badge>
          )
        },
      },
      AddOns: {
        id: "AddOns",
        label: "Add-ons",
        description:
          "The customer's add-ons that go with this license: extras on top of it, working while it is live, for the people on it. Added and removed in the Add-ons section under this table.",
        defaultSize: 150,
        getValue: (row) =>
          (addOnsByLicenseId.get(row.Id) ?? [])
            .map((addOn) => addOn.AddOnName)
            .join(", "),
        renderCell({ row }) {
          const addOns = addOnsByLicenseId.get(row.original.Id) ?? []
          if (addOns.length === 0) return null
          return (
            <div className="flex flex-wrap gap-1">
              {addOns.map((addOn) => (
                <Badge
                  key={addOn.AddOnId}
                  variant="neutral"
                  tooltip={
                    addOn.FeatureGroupNames
                      ? `Goes with this license. Unlocks ${addOn.FeatureGroupNames}. Managed in the Add-ons section below.`
                      : "Goes with this license. Unlocks nothing yet: no package is on this add-on. Managed in the Add-ons section below."
                  }
                >
                  {addOn.AddOnName}
                </Badge>
              ))}
            </div>
          )
        },
      },
      RenewsAt: {
        id: "RenewsAt",
        label: "Renews",
        defaultSize: 90,
        defaultHidden: true,
        renderCell({ row }) {
          const value = row.original.RenewsAt
          return value ? formatDateString(value) : null
        },
      },
      CreatedAt: {
        id: "CreatedAt",
        label: "Created",
        defaultSize: 90,
        defaultHidden: true,
        renderCell({ row }) {
          const value = row.original.CreatedAt
          return value ? formatDateString(value) : null
        },
      },
      Id: { id: "Id", label: "Id", defaultSize: 50, defaultHidden: true },
    },
  })
}

export type LicenseTableProps = Omit<
  TableProps<LicenseFragment>,
  "data" | "columns"
> & {
  licenses: LicenseFragment[]
  isLoading?: boolean
  error?: Error
  /** Scopes the toolbar's grant button. */
  customerId?: number
  customerName?: string
  /** The customer's add-ons with the licenses that carry each, for the Add-ons column. */
  customerAddOns?: {
    AddOnId: number
    AddOnName: string
    FeatureGroupNames?: string | null
    Carriers: { LicenseId: number }[]
  }[]
  /** Seats held across the customer's seated licenses, for the Seat holders column. */
  licenseSeatHolders?: LicenseSeatHolder[]
  onRefresh?(): void
}

// One customer's licenses inside the panel's Licenses tab. A customer holds
// a handful of licenses at most, so there is no status filter bar here —
// status worklists (trials ending, expired freeloaders) live on the
// customers board's pills, which filter across the whole book.
function LicenseTable(props: LicenseTableProps) {
  const {
    className,
    licenses,
    isLoading,
    error,
    customerId,
    customerName,
    customerAddOns,
    licenseSeatHolders,
    onRefresh,
    ...tableProps
  } = props

  const { data: statusesData, error: statusesError } = useLicenseStatusesQuery()
  errorToast(statusesError, { title: "Failed to query for license statuses" })
  const statuses = statusesData?.getLicenseStatuses ?? []

  const statusNamesByCode = useMemo(
    () => new Map(statuses.map((status) => [status.Code, status.Name])),
    [statuses],
  )

  // One add-on can go with two of the customer's licenses (Dataset write
  // with the Dataset MCP and the Norway MCP); it shows on both rows.
  const addOnsByLicenseId = useMemo(() => {
    const map = new Map<number, LicenseAddOnGrant[]>()
    for (const addOn of customerAddOns ?? []) {
      for (const carrier of addOn.Carriers) {
        const addOns = map.get(carrier.LicenseId) ?? []
        addOns.push(addOn)
        map.set(carrier.LicenseId, addOns)
      }
    }
    return map
  }, [customerAddOns])

  const seatHoldersByLicenseId = useMemo(
    () => groupSeatHoldersByLicense(licenseSeatHolders ?? []),
    [licenseSeatHolders],
  )

  const columns = useMemo(
    () =>
      createLicenseColumns({
        statusNamesByCode,
        addOnsByLicenseId,
        seatHoldersByLicenseId,
      }),
    [statusNamesByCode, addOnsByLicenseId, seatHoldersByLicenseId],
  )

  return (
    <Table
      className={cn("license-table", className)}
      tableId="customer-license-table"
      data={licenses}
      columns={columns}
      {...tableProps}
    >
      <Table.Toolbar>
        <div className="flex-1" />
        <Table.ToolbarFilters />
        <Separator orientation="vertical" />

        <Table.ToolbarMenu onRefresh={onRefresh} exportFileName="licenses">
          {customerId && (
            <LicenseCreateButton
              customerId={customerId}
              customerName={customerName}
            />
          )}
        </Table.ToolbarMenu>
      </Table.Toolbar>

      <Table.FilterList />

      <Table.RowToolbar<LicenseFragment> column="PlanName">
        {(license) => <LicenseTableRowToolbar license={license} />}
      </Table.RowToolbar>

      <Table.Content isLoading={isLoading} error={error} />
    </Table>
  )
}

export default LicenseTable
